import { jsPDF } from 'jspdf';

/**
 * Build EX-10 style PDF from case section field pairs (advisor case detail).
 * When isDraft, draws a large diagonal DRAFT watermark behind the text.
 */
export function buildEx10Pdf({
  isDraft,
  caseDisplayId,
  applicantName,
  formCode = 'EX-10',
  firstName = '',
  lastName = '',
  secondSurname = '',
  identityPairs = [],
  contactPairs = [],
  addressPairs = [],
  representativePairs = [],
  notificationPairs = [],
}) {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 14;
  const maxW = pageW - margin * 2;

  if (isDraft) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(52);
    doc.setTextColor(255, 210, 140);
    doc.text('DRAFT', pageW / 2, pageH / 2, { align: 'center', angle: 32 });
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'normal');
  }

  let y = 18;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(15);
  doc.text(String(formCode || 'EX-10'), pageW / 2, y, { align: 'center' });
  y += 7;
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(80, 80, 80);
  doc.text(
    'Solicitud de autorizacion - circunstancias excepcionales',
    pageW / 2,
    y,
    { align: 'center', maxWidth: maxW },
  );
  doc.setTextColor(0, 0, 0);
  y += 10;

  doc.setFontSize(9);
  doc.text(`Case: ${caseDisplayId || '-'}`, margin, y);
  doc.text(`Applicant: ${applicantName && applicantName !== '-' ? applicantName : '-'}`, margin + 75, y);
  y += 8;

  const ensureSpace = (needed) => {
    if (y + needed > pageH - 16) {
      doc.addPage();
      y = 18;
    }
  };

  const writeSection = (title, pairs) => {
    if (!pairs.length) return;
    ensureSpace(12);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text(String(title).toUpperCase(), margin, y);
    y += 5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    for (const [label, value] of pairs) {
      const text = `${label}: ${value == null || value === '' ? '' : String(value)}`;
      const lines = doc.splitTextToSize(text, maxW);
      ensureSpace(lines.length * 4.5 + 2);
      doc.text(lines, margin, y);
      y += lines.length * 4.5 + 1;
    }
    y += 4;
  };

  writeSection('Identity', identityPairs);
  writeSection('Contact', contactPairs);
  writeSection('Address', addressPairs);
  writeSection('Representative', representativePairs);
  writeSection('Notification address', notificationPairs);

  doc.setFontSize(8);
  doc.setTextColor(120, 120, 120);
  ensureSpace(8);
  doc.text(
    'Generated for advisor review. Official submission must follow consulate / immigration office instructions.',
    margin,
    pageH - 10,
    { maxWidth: maxW },
  );

  const toSlugPart = (value) =>
    String(value || '')
      .trim()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-zA-Z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .toLowerCase();
  const resolvedLast = toSlugPart(lastName);
  const resolvedSecond = toSlugPart(secondSurname);
  const resolvedFirst = toSlugPart(firstName);
  const nameParts = [resolvedLast, resolvedSecond, resolvedFirst].filter(Boolean);
  const fallback = String(caseDisplayId || 'case')
    .replace(/[^a-zA-Z0-9_-]+/g, '_')
    .slice(0, 40);
  const filename = `${(nameParts.length ? nameParts.join('-') : fallback)}.pdf`;

  return { blob: doc.output('blob'), filename };
}
